import {useCallback, useMemo} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {
    buildCoverageModalSearch,
    readCoverageModalState,
    stripCoverageModalParams,
} from './coverageModalUrlState';
import {stripCollectionMaterialViewerParams} from '../../collection/viewer/openCollectionMaterial';

/**
 * URL-driven state for the Collection / Analytic coverage modals.
 * Opening the modal pushes a history entry; in-modal changes (tab, anchors)
 * replace it so browser Back closes the modal instead of replaying every click.
 */
export default function useCoverageModalUrlState() {
    const navigate = useNavigate();
    const location = useLocation();

    const state = useMemo(() => readCoverageModalState(location.search), [location.search]);

    const openModal = useCallback((next) => {
        navigate({
            pathname: location.pathname,
            search: buildCoverageModalSearch(location.search, next),
            hash: location.hash,
        });
    }, [navigate, location]);

    const updateModal = useCallback((next) => {
        navigate({
            pathname: location.pathname,
            search: buildCoverageModalSearch(location.search, next),
            hash: location.hash,
        }, {replace: true});
    }, [navigate, location]);

    const closeModal = useCallback(() => {
        navigate({
            pathname: location.pathname,
            search: stripCoverageModalParams(location.search),
            hash: location.hash,
        });
    }, [navigate, location]);

    /** Relative URL of the exact current UI state — feed to C2 returnTo. */
    const currentReturnTo = useMemo(() => {
        const search = stripCollectionMaterialViewerParams(location.search);
        return `${location.pathname}${search}${location.hash || ''}`;
    }, [location]);

    return {state, openModal, updateModal, closeModal, currentReturnTo};
}
